/**
 * Port of `HexagonalColorHue` from react-hexagon-color-picker (MIT, crypto-red).
 * @see https://github.com/crypto-red/react-hexagon-color-picker
 */
import { memo, useId } from 'react';

import { hslToHex } from './hsl';

const HUE_PATH_DS = [
  'M18 15l-9 5-9-5V5l9-5 9 5z',
  'M27 30l-9 5-9-5V20l9-5 9 5z',
  'M36 15l-9 5-9-5V5l9-5 9 5z',
  'M45 30l-9 5-9-5V20l9-5 9 5z',
  'M54 15l-9 5-9-5V5l9-5 9 5z',
  'M63 30l-9 5-9-5V20l9-5 9 5z',
  'M72 15l-9 5-9-5V5l9-5 9 5z',
  'M81 30l-9 5-9-5V20l9-5 9 5z',
  'M90 15l-9 5-9-5V5l9-5 9 5z',
  'M99 30l-9 5-9-5V20l9-5 9 5z',
  'M108 15l-9 5-9-5V5l9-5 9 5z',
  'M117 30l-9 5-9-5V20l9-5 9 5z',
  'M126 15l-9 5-9-5V5l9-5 9 5z',
  'M135 30l-9 5-9-5V20l9-5 9 5z',
  'M144 15l-9 5-9-5V5l9-5 9 5z',
  'M153 30l-9 5-9-5V20l9-5 9 5z',
  'M162 15l-9 5-9-5V5l9-5 9 5z',
  'M171 30l-9 5-9-5V20l9-5 9 5z',
  'M180 15l-9 5-9-5V5l9-5 9 5z',
  'M189 30l-9 5-9-5V20l9-5 9 5z',
  'M198 15l-9 5-9-5V5l9-5 9 5z',
  'M207 30l-9 5-9-5V20l9-5 9 5z',
  'M216 15l-9 5-9-5V5l9-5 9 5z',
  'M225 30l-9 5-9-5V20l9-5 9 5z',
  'M234 15l-9 5-9-5V5l9-5 9 5z',
] as const;

export type HueStripElement = { d: string; fill: string; hue: number };

function buildHuePaths(): HueStripElement[] {
  return HUE_PATH_DS.map((d, index, array) => {
    if (index === 0) {
      return { d, fill: '#000000', hue: 0 };
    }
    const hueDeg = (360 * index) / (array.length - 1);
    return {
      d,
      fill: hslToHex(hueDeg / 3.6, 100, 50),
      hue: hueDeg,
    };
  });
}

const HUE_PATHS = buildHuePaths();

function nearestStripHue(h: number): number {
  let best = HUE_PATHS[0].hue;
  let bestD = Number.POSITIVE_INFINITY;
  for (const p of HUE_PATHS) {
    const d = Math.abs(h - p.hue);
    const circ = Math.min(d, 360 - d);
    if (circ < bestD) {
      bestD = circ;
      best = p.hue;
    }
  }
  return best;
}

export type HexagonalColorHueProps = React.SVGProps<SVGSVGElement> & {
  hue: number;
  onHueChange?: (hue: number) => void;
  border?: string | null;
  highlight?: string | null;
};

const HexagonalColorHueBase = ({
  hue,
  onHueChange,
  border = null,
  highlight = null,
  ...svgProps
}: HexagonalColorHueProps) => {
  const clipId = useId().replace(/:/g, '');

  const hueSnap = nearestStripHue(hue);

  const borderProps =
    border !== null
      ? {
          vectorEffect: 'non-scaling-stroke' as const,
          strokeWidth: 1,
          stroke: border,
          strokeLinejoin: 'miter' as const,
          strokeLinecap: 'square' as const,
          strokeMiterlimit: 2,
        }
      : {};

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      style={{
        isolation: 'isolate',
        pointerEvents: 'none',
        height: '100%',
        width: '100%',
      }}
      viewBox="0 0 234 35"
      width={234}
      height={35}
      {...svgProps}
    >
      <defs>
        <clipPath id={clipId}>
          <path d="M0 0h234v35H0z" />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        {HUE_PATHS.map((element, index) => {
          const isSelected = element.hue === hueSnap && highlight !== null && highlight !== '';
          const elementBorderProps = isSelected && highlight ? { ...borderProps, stroke: highlight } : borderProps;

          return (
            <path
              {...elementBorderProps}
              key={`hue_path_${index}`}
              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={() => {
                onHueChange?.(element.hue);
              }}
              fill={element.fill}
              d={element.d}
            />
          );
        })}
      </g>
    </svg>
  );
};

export const HexagonalColorHue = memo(HexagonalColorHueBase);
